import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Pagination,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import { Visibility, FilterList, Clear, Download } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { formatDateTimeWithTimezone, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const FeedbackList = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const tzLabel = getTimezoneLabel(displayTimezone);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    priority: '',
    status: '',
    amApprovalStatus: '',
    startDate: null,
    endDate: null,
    submittedBy: '',
    search: ''
  });
  const [users, setUsers] = useState([]);
  const [sort, setSort] = useState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const fetchGenerationRef = useRef(0);

  // Memoize filters to prevent unnecessary re-renders
  const filtersString = useMemo(() => JSON.stringify(filters), [
    filters.type,
    filters.priority,
    filters.status,
    filters.startDate?.getTime(),
    filters.endDate?.getTime(),
    filters.submittedBy,
    filters.search,
    filters.amApprovalStatus
  ]);

  useEffect(() => {
    const initial = location.state?.feedbackFilters;
    if (!initial) return;
    setFilters((prev) => ({
      ...prev,
      type: initial.type || prev.type,
      amApprovalStatus: initial.amApprovalStatus || prev.amApprovalStatus,
      status: initial.status || prev.status
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Memoize fetchFeedback to prevent recreation on every render
  const fetchFeedback = useCallback(async (signal = null) => {
    const generation = ++fetchGenerationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
        )
      };

      // Convert dates to ISO strings (display TZ calendar date -> UTC range when displayTimezone set)
      if (filters.startDate) {
        const d = dayjs(filters.startDate);
        params.startDate = displayTimezone ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '00:00', displayTimezone) ?? d.toISOString()) : d.toISOString();
      }
      if (filters.endDate) {
        const d = dayjs(filters.endDate);
        params.endDate = displayTimezone ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '23:59', displayTimezone) ?? d.toISOString()) : d.toISOString();
      }

      const config = signal ? { params, signal } : { params };
      const response = await axios.get('/api/v1/feedback', config);

      if (signal?.aborted || generation !== fetchGenerationRef.current) return;

      // Handle response - check if it has the expected structure
      if (response.data) {
        setFeedback(response.data.feedback || []);

        // Safely update pagination
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || 0,
            pages: response.data.pagination.pages || 0
          }));
        } else {
          // If no pagination object, assume single page
          setPagination(prev => ({
            ...prev,
            total: response.data.feedback?.length || 0,
            pages: 1
          }));
        }
      } else {
        // Fallback if response structure is unexpected
        setFeedback([]);
        setPagination(prev => ({ ...prev, total: 0, pages: 0 }));
      }
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      if (generation !== fetchGenerationRef.current) return;
      console.error('Error fetching feedback:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load feedback';
      showToast(errorMessage, 'error');

      // Set empty state on error
      setFeedback([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 0 }));
    } finally {
      if (generation === fetchGenerationRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filtersString, sort.sortBy, sort.sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, []); // Only fetch users once on mount

  useEffect(() => {
    const controller = new AbortController();
    fetchFeedback(controller.signal);
    return () => controller.abort();
  }, [fetchFeedback]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const handleFilterChange = (field) => (event) => {
    const value = event.target ? event.target.value : event;
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };

  const handleDateChange = (field) => (date) => {
    setFilters(prev => ({ ...prev, [field]: date }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      priority: '',
      status: '',
      amApprovalStatus: '',
      startDate: null,
      endDate: null,
      submittedBy: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleSort = (columnId) => {
    if (columnId === 'amApproval') return;
    setSort(prev => ({
      sortBy: columnId,
      sortOrder: prev.sortBy === columnId && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const sortableColumns = [
    { id: 'feedbackId', label: 'Feedback ID' },
    { id: 'type', label: 'Type' },
    { id: 'amApproval', label: 'AM Approval' },
    { id: 'priority', label: 'Priority' },
    { id: 'severity', label: 'Severity' },
    { id: 'status', label: 'Status' },
    { id: 'assignedTo', label: 'Assigned To' },
    { id: 'submittedBy', label: 'Submitted By' },
    { id: 'createdAt', label: 'Date' }
  ];

  // Escape a value for CSV (wrap in quotes if contains comma, newline, or quote)
  const escapeCsvValue = (value) => {
    if (value == null || value === '') return '';
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const formatStatusHistory = (statusHistory) => {
    if (!Array.isArray(statusHistory) || statusHistory.length === 0) return '';
    return statusHistory
      .map((h) => {
        const date = h.changedAt ? new Date(h.changedAt).toISOString() : '';
        const by = h.changedBy
          ? [h.changedBy.firstName, h.changedBy.lastName, h.changedBy.email].filter(Boolean).join(' ')
          : '';
        const notes = h.notes ? `: ${h.notes}` : '';
        return `${h.status} @ ${date} by ${by}${notes}`.trim();
      })
      .join('; ');
  };

  const formatComments = (comments) => {
    if (!Array.isArray(comments) || comments.length === 0) return '';
    return comments
      .map((c) => {
        const date = c.commentedAt ? new Date(c.commentedAt).toISOString() : '';
        const by = c.commentedBy
          ? [c.commentedBy.firstName, c.commentedBy.lastName, c.commentedBy.email].filter(Boolean).join(' ')
          : '';
        const edited = c.editedAt ? ` (edited ${new Date(c.editedAt).toISOString()})` : '';
        return `${c.comment} — by ${by} @ ${date}${edited}`.trim();
      })
      .join('; ');
  };

  const formatAttachments = (attachments) => {
    if (!Array.isArray(attachments) || attachments.length === 0) return '';
    return attachments
      .map((a) => {
        const part = a.url ? `${a.filename || 'file'} (${a.url})` : (a.filename || 'file');
        return a.size != null ? `${part} ${a.size} bytes` : part;
      })
      .join('; ');
  };

  const exportToCsv = useCallback(async () => {
    try {
      showToast('Preparing export...', 'info');
      const limit = 100;
      let allFeedback = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const params = {
          page,
          limit,
          ...Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
          )
        };
        if (filters.startDate) {
          const d = dayjs(filters.startDate);
          params.startDate = displayTimezone ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '00:00', displayTimezone) ?? d.toISOString()) : d.toISOString();
        }
        if (filters.endDate) {
          const d = dayjs(filters.endDate);
          params.endDate = displayTimezone ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '23:59', displayTimezone) ?? d.toISOString()) : d.toISOString();
        }

        const response = await axios.get('/api/v1/feedback', { params });
        const batch = response.data.feedback || [];
        allFeedback = allFeedback.concat(batch);
        const total = response.data.pagination?.total ?? batch.length;
        hasMore = allFeedback.length < total;
        page += 1;
      }

      if (allFeedback.length === 0) {
        showToast('No feedback to export', 'warning');
        return;
      }

      const headers = [
        'Feedback ID',
        'Internal ID',
        'Client ID',
        'Type',
        'Priority',
        'Severity',
        'Status',
        'Assigned To',
        'Description',
        'Component',
        'Environment',
        'Version',
        'Submitted By Name',
        'Submitted By Email',
        'Created At',
        'Updated At',
        'Voice Recording URL',
        'Screenshot URL',
        'Attachments',
        'Status History',
        'Comments',
        'Public Token'
      ];

      const rows = allFeedback.map((item) => [
        escapeCsvValue(item.feedbackId),
        escapeCsvValue(item._id),
        escapeCsvValue(
          item.clientId != null
            ? (typeof item.clientId === 'object' && item.clientId?._id != null
                ? item.clientId._id
                : item.clientId)
            : ''
        ),
        escapeCsvValue(item.type),
        escapeCsvValue(item.priority),
        escapeCsvValue(item.severity),
        escapeCsvValue(item.status),
        escapeCsvValue(item.assignedTo || 'Support Team'),
        escapeCsvValue(item.description),
        escapeCsvValue(item.component),
        escapeCsvValue(item.environment),
        escapeCsvValue(item.version),
        escapeCsvValue(
          item.submittedBy
            ? `${item.submittedBy.firstName || ''} ${item.submittedBy.lastName || ''}`.trim()
            : 'Anonymous'
        ),
        escapeCsvValue(item.submittedBy?.email ?? ''),
        escapeCsvValue(item.createdAt ? new Date(item.createdAt).toISOString() : ''),
        escapeCsvValue(item.updatedAt ? new Date(item.updatedAt).toISOString() : ''),
        escapeCsvValue(item.voiceRecordingUrl ?? ''),
        escapeCsvValue(item.screenshotUrl ?? ''),
        escapeCsvValue(formatAttachments(item.attachments)),
        escapeCsvValue(formatStatusHistory(item.statusHistory)),
        escapeCsvValue(formatComments(item.comments)),
        escapeCsvValue(item.publicToken ?? '')
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `feedback-export-${dayjs().format('YYYY-MM-DD-HHmm')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      showToast(`Exported ${allFeedback.length} feedback item(s) to CSV`, 'success');
    } catch (error) {
      console.error('Error exporting feedback:', error);
      showToast(
        error.response?.data?.message || error.message || 'Failed to export feedback',
        'error'
      );
    }
  }, [filters, showToast]);

  const getStatusColor = (status) => {
    const colors = {
      'New': 'default',
      'In Progress': 'info',
      'Fixed': 'success',
      'Closed': 'secondary',
      'Reopened': 'warning',
      'On Hold': 'warning'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Blocker': 'error',
      'Critical': 'error',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'default'
    };
    return colors[priority] || 'default';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
          <Typography variant="h4">
            Operational Queries
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportToCsv}
            disabled={loading}
          >
            Export to CSV
          </Button>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={filters.search}
                onChange={handleFilterChange('search')}
                placeholder="Search description..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={handleFilterChange('type')}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="New Feature">New Feature</MenuItem>
                  <MenuItem value="Recommendation">Recommendation</MenuItem>
                  <MenuItem value="I dont know">I dont know</MenuItem>
                  <MenuItem value="Bug">Bug</MenuItem>
                  <MenuItem value="Improvement">Improvement</MenuItem>
                  <MenuItem value="Task">Task</MenuItem>
                  <MenuItem value="Change Request Form">Change Request Form</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 130 }}>
                <InputLabel>AM Approval</InputLabel>
                <Select
                  value={filters.amApprovalStatus}
                  onChange={handleFilterChange('amApprovalStatus')}
                  label="AM Approval"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 95 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={handleFilterChange('priority')}
                  label="Priority"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Blocker">Blocker</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 115 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Fixed">Fixed</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                  <MenuItem value="Reopened">Reopened</MenuItem>
                  <MenuItem value="On Hold">On Hold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={tzLabel ? `Start Date (${tzLabel})` : 'Start Date'}
                value={filters.startDate ? dayjs(filters.startDate) : null}
                onChange={(date) => handleDateChange('startDate')(date ? date.toDate() : null)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={tzLabel ? `End Date (${tzLabel})` : 'End Date'}
                value={filters.endDate ? dayjs(filters.endDate) : null}
                onChange={(date) => handleDateChange('endDate')(date ? date.toDate() : null)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Submitted By</InputLabel>
                <Select
                  value={filters.submittedBy}
                  onChange={handleFilterChange('submittedBy')}
                  label="Submitted By"
                >
                  <MenuItem value="">All</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearFilters}
                fullWidth
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {sortableColumns.map((col) => (
                  <TableCell key={col.id} sortDirection={sort.sortBy === col.id ? sort.sortOrder : false}>
                    <TableSortLabel
                      active={sort.sortBy === col.id}
                      direction={sort.sortBy === col.id ? sort.sortOrder : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : feedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No feedback found
                  </TableCell>
                </TableRow>
              ) : (
                feedback.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          textDecoration: 'underline'
                        }}
                        onClick={() => navigate(`/feedback/${item._id}`)}
                      >
                        {item.feedbackId}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>
                      {item.amApproval?.required ? (
                        <Chip
                          label={item.amApproval.status || 'PENDING'}
                          size="small"
                          color={
                            item.amApproval.status === 'APPROVED'
                              ? 'success'
                              : item.amApproval.status === 'REJECTED'
                                ? 'error'
                                : 'warning'
                          }
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.priority}
                        color={getPriorityColor(item.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.severity}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.assignedTo || 'Support Team'}</TableCell>
                    <TableCell>
                      {item.submittedBy
                        ? `${item.submittedBy.firstName} ${item.submittedBy.lastName}`
                        : 'Anonymous'}
                    </TableCell>
                    <TableCell>
                      {displayTimezone ? formatDateTimeWithTimezone(item.createdAt, null, displayTimezone) : new Date(item.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/feedback/${item._id}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default FeedbackList;

